import { inject, injectable } from 'inversify'
import { isString, clone } from 'lodash-es'
import type { Query, Record as VuexOrmRecord } from '@vuex-orm/core'
import type * as Contracts from '@vuex-orm/core/dist/src/query/contracts'
import type BaseModel from '@/data/models/BaseModel'
import { SERVICE_TYPES } from '@/core/container/types'
import type { ModelRaw, Nullable } from '@/types'
import type { RepositorySettings } from '@/decorators/repositoryDecorators'
import ModelMapper from '@/data/utils/modelMapper'
import type LoggerService from '@/services/loggerService'
import type { LowLevelInsertBody, LowLevelUpdateBody, LowLevelUpdateByConditionBody } from '@/services/vuex/types'
import type { RecordUpdateClosure } from '@/services/vuex/mutators/baseMutations'
import { TmRepositoryError } from '@/core/error/tmRepositoryError'
import type { ModelRelationArray } from '@/services/domain/types'
import type { IDataProvider } from '@/services/types'
import { TmEntityNotFoundError } from '@/core/error/tmEntityNotFoundError'
import { isDev } from '@/utils/system'
import { getMonitoringService } from '@/core/container/ioc'
import TmLogicError from '@/core/error/tmLogicError'
import { wait } from '@/utils/async'

@injectable()
export default abstract class ORMBaseRepository<T extends BaseModel, R extends string> implements IDataProvider<T> {
  protected constructor(@inject(SERVICE_TYPES.LoggerService) protected readonly loggerService: LoggerService) {}

  /**
   * Repo Settings
   */
  public settings(): RepositorySettings<R> {
    throw new TmRepositoryError('Please use decorator @RepoSettings<Endpoint>')
  }

  public isPublic() {
    return false
  }

  protected model() {
    return this.settings().model
  }

  public findEntityByIdOrFail(t: string, withRelations?: ModelRelationArray<T>): T {
    const entity = this.findEntityByIdOrNull(t, withRelations)
    if (entity === null) {
      throw new TmEntityNotFoundError(t)
    }
    return entity
  }

  public findEntityByIdOrNull(t: string, withRelations: ModelRelationArray<T> = []): T | null {
    const model = withRelations.length ? this.query().with(withRelations).find(t) : (this.model().find(t) as T)
    return model ?? null
  }

  public findEntityOrFail(t: (entity: T) => boolean, withRelations?: ModelRelationArray<T>): T {
    const entity = this.findEntityOrNull(t)
    if (entity === null) {
      throw new TmEntityNotFoundError(t)
    }
    return entity
  }

  public findEntityOrNull(t: (entity: T) => boolean, withRelations: ModelRelationArray<T> = []): T | null {
    const model = withRelations.length
      ? this.query().with(withRelations).where(t).first()
      : (this.query().withAll().where(t).first() as T)
    return model ?? null
  }

  public firstOrFail(): T {
    const entity = this.firstOrNull()
    if (entity === null) {
      throw new TmEntityNotFoundError()
    }
    return entity
  }

  public firstOrNull(): T | null {
    const entity = this.model().all()[0] as T | undefined
    return entity ?? null
  }

  /**
   * ORM methods
   */
  public all() {
    return this.model().all() as unknown as Array<T>
  }

  public query() {
    return this.model().query() as Query<T>
  }

  public findIn(ids: string[], withRelations: ModelRelationArray<T> | ['*'] = []): T[] {
    return this.query().with(withRelations).findIn(ids) as unknown as T[]
  }

  public insert(newRecords: LowLevelInsertBody<T>[], stiModel?: typeof BaseModel) {
    this.log('insert')
    this.logArgs(JSON.stringify(clone(newRecords), null, 2))
    this.lowLevelInsert(newRecords, stiModel)
    return newRecords
  }

  public fillResponseSerializer(data: unknown, _id: string): ModelRaw<T> {
    return data as ModelRaw<T>
  }

  public abstract getRequest(id: string): Promise<ModelRaw<T>>

  public async fill(id: string, withRelations: ModelRelationArray<T> = []): Promise<T> {
    this.logGroupStart('fill')
    this.logArgs(id)
    const model = this.fillResponseSerializer(await this.getRequest(id), id)
    await this.insertOrUpdateItem(model)
    this.logGroupEnd()
    return this.findEntityByIdOrFail(id, withRelations)
  }

  public abstract fillAll(isReset?: boolean): Promise<T[]>

  public async findOrFill(id: string, withRelations: ModelRelationArray<T> = []): Promise<T> {
    const item = this.findEntityByIdOrNull(id, withRelations)
    if (item) {
      return item
    }
    this.log('findOrFill')
    this.logArgs(id)
    return this.fill(id, withRelations)
  }

  public update(changeSet: LowLevelUpdateBody<T>[], stiModel?: typeof BaseModel) {
    this.logGroupStart('update')
    this.logArgs(JSON.stringify(changeSet, null, 2))
    this.log('update')
    this.lowLevelUpdate(changeSet, stiModel)
    this.logGroupEnd()
  }

  /**
   * Low level update
   * - NOT USE FOR UPDATE ENTITIES WITH RELATIONS!
   */
  public updateItem(changeSet: LowLevelUpdateBody<T>, stiModel?: typeof BaseModel) {
    this.update([changeSet], stiModel)
  }

  public async updateItemIfExists(changeSet: LowLevelUpdateBody<T>) {
    if (!this.findEntityByIdOrNull(changeSet.id)) {
      return
    }
    await this.insertOrUpdateItem(changeSet)
  }

  public async bulkUpdateByIds(ids: string[], changeSet: Partial<ModelRaw<T>>, stiModel?: typeof BaseModel) {
    const idsChangeSet: LowLevelUpdateBody<T>[] = ids.map((id) => ({
      ...changeSet,
      id,
    }))
    await this.insertOrUpdate(idsChangeSet)
  }

  public updateByCondition(
    updateData: LowLevelUpdateByConditionBody<T> | RecordUpdateClosure<T>,
    condition: Contracts.Predicate<T>,
    stiModel?: typeof BaseModel,
  ) {
    this.logGroupStart('updateByCondition')
    this.logArgs(JSON.stringify(updateData, null, 2))
    this.log('updateByCondition')
    this.lowLevelUpdateByCondition<LowLevelUpdateByConditionBody<T> | RecordUpdateClosure<T>>(
      updateData,
      condition,
      stiModel,
    )
    this.logGroupEnd()
  }

  public async insertItems(payload: LowLevelUpdateBody<T>[]) {
    this.log('insertOrUpdate')
    this.logArgs(JSON.stringify(payload, null, 2))
    return this.model().insert({ data: payload })
  }

  public async insertOrUpdate(payload: LowLevelUpdateBody<T>[]) {
    this.log('insertOrUpdate')
    this.logArgs(JSON.stringify(payload, null, 2))
    return this.model().insertOrUpdate({ data: payload })
  }

  public insertOrUpdateItem(payload: LowLevelUpdateBody<T>) {
    return this.insertOrUpdate([payload])
  }

  /**
   * Insert or update (if exists) record
   * Pay attention. This method cannot hidrate data to child models
   * @param payload Data array
   * @param stiModel Children model name for STI
   */
  public insertOrUpdateRaw(payload: LowLevelUpdateBody<T>[], stiModel?: typeof BaseModel) {
    this.log('insertOrUpdateRaw')
    this.logArgs(JSON.stringify(payload, null, 2))
    this.lowLevelInsertOrUpdate(payload, stiModel)
  }

  public create(payload: VuexOrmRecord | VuexOrmRecord[]) {
    this.log('create')
    this.logArgs(JSON.stringify(payload))
    return this.model().create({ data: payload })
  }

  public delete(ids: string[], stiModel?: typeof BaseModel) {
    // this.log('delete')
    // this.logArgs(JSON.stringify(ids))
    this.lowLevelDelete(ids, stiModel)
  }

  public async deleteAll() {
    this.log('deleteAll')
    return this.model().deleteAll()
  }

  public deleteByCondition(condition: Contracts.Predicate<T>, stiModel?: typeof BaseModel) {
    // this.log('deleteByCondition')
    // this.logArgs(JSON.stringify(condition))
    this.lowLevelDeleteByCondition(condition, stiModel)
  }

  public getRaw(id: string): ModelRaw<T> {
    const { entity } = this.getBaseEntity()
    const { state } = this.getBaseEntity().store()
    const raws = state.entities[entity].data as { [k: string]: ModelRaw<T> }

    if (raws) {
      return raws[id]
    }

    throw new TmRepositoryError('Not found')
  }

  public getRaws(ids: string[], localKey = 'id'): ModelRaw<T>[] {
    const { state } = this.getBaseEntity().store()
    const raws = state.entities[this.getBaseEntity().entity].data as { [k: string]: ModelRaw<T> }
    if (raws) {
      return Object.values(raws).filter((item: ModelRaw<T>) => ids.indexOf(item[localKey]) >= 0)
    }

    throw new TmRepositoryError('Not found')
  }

  public getAllRaw(): ModelRaw<T>[] {
    const { state } = this.getBaseEntity().store()
    const raws = state.entities[this.getBaseEntity().entity].data as Record<string, ModelRaw<T>>
    return Object.values(raws)
  }

  private modelKeysWithRelations: Nullable<string[]> = null

  private checkRelatedModelsLowLevelOperationLogged = false

  /**
   * The method must be asynchronous so as not to block the main code.
   */
  private async checkRelatedModelsLowLevelOperation() {
    // adding wait to make the method asynchronous
    await wait()
    const model = this.model()
    if (this.modelKeysWithRelations === null) {
      const modelFields = model.getFields()
      const modelKeys = Object.keys(modelFields)
      this.modelKeysWithRelations = modelKeys.filter((modelKey) => {
        const modelField = modelFields[modelKey]
        return !!(modelField as any).foreignKey
      })
    }

    if (this.modelKeysWithRelations.length === 0) {
      return []
    }
    const warningMessage = `Please, not use low level method for update entity with relations. Model name="${
      model.name
    }", related models="${this.modelKeysWithRelations.toString()}"`
    this.loggerService.log('warn', warningMessage)
    if (!this.checkRelatedModelsLowLevelOperationLogged) {
      getMonitoringService().logInfo(warningMessage)
      this.checkRelatedModelsLowLevelOperationLogged = true
    }
    return this.modelKeysWithRelations
  }

  private checkLowLevelOperationFieldsLogged: string[] = []

  private async checkLowLevelOperation<M extends object = T>(records: M[]) {
    const model = this.model()
    const modelKeysWithRelations = await this.checkRelatedModelsLowLevelOperation()
    if (modelKeysWithRelations.length === 0) {
      return
    }
    records.forEach((record) => {
      Object.keys(record).forEach((t) => {
        if (!modelKeysWithRelations.includes(t) || record[t] === null) {
          return
        }
        const errorMessage = `The low level method cannot update related entities. Model name="${
          model.name
        }", related models="${modelKeysWithRelations.toString()}", field name="${t}"`
        if (isDev) {
          throw new TmLogicError(errorMessage)
        } else if (!this.checkLowLevelOperationFieldsLogged.includes(t)) {
          getMonitoringService().logError(new Error(errorMessage))
          this.checkLowLevelOperationFieldsLogged.push(t)
        }
      })
    })
  }

  protected lowLevelInsert<M extends object = T>(records: M[], stiModel?: typeof BaseModel) {
    this.checkLowLevelOperation<M>(records)
    this.log('lowLevelInsert')
    this.logArgs(JSON.stringify(records, null, 2))
    this.model()
      .store()
      .commit(this.getMutationName('LOW_LEVEL_INSERT'), {
        stiModel: stiModel || this.model(),
        data: ModelMapper.mappingWithDefault<M>(stiModel || this.model(), records),
      })
  }

  protected lowLevelUpdate<M extends object = T>(records: M[], stiModel?: typeof BaseModel) {
    this.checkLowLevelOperation<M>(records)
    this.log('lowLevelUpdate')
    this.logArgs(JSON.stringify(records, null, 2))
    this.model().store().commit(this.getMutationName('LOW_LEVEL_UPDATE'), {
      stiModel,
      data: records,
    })
  }

  protected lowLevelUpdateByCondition<M = T>(
    updateTo: M | RecordUpdateClosure<T>,
    condition: Contracts.Predicate<T>,
    stiModel?: typeof BaseModel,
  ) {
    this.checkRelatedModelsLowLevelOperation()
    this.model().store().commit(this.getMutationName('LOW_LEVEL_UPDATE_CONDITION'), {
      stiModel,
      condition,
      data: updateTo,
    })
  }

  protected lowLevelInsertOrUpdate(records: LowLevelUpdateBody<T>[], stiModel?: typeof BaseModel) {
    this.checkRelatedModelsLowLevelOperation()
    this.log('lowLevelInsertOrUpdate')
    this.logArgs(JSON.stringify(records, null, 2))
    this.model()
      .store()
      .commit(this.getMutationName('LOW_LEVEL_INSERT_OR_UPDATE'), {
        stiModel,
        data: records.map((r) => {
          if (this.findEntityByIdOrNull(r.id)) {
            return r
          }
          return ModelMapper.mappingWithDefault<LowLevelUpdateBody<T>>(stiModel || this.model(), [r])[0]
        }),
      })
  }

  protected lowLevelDelete(ids: (string | number)[], stiModel?: typeof BaseModel) {
    this.model().store().commit(this.getMutationName('LOW_LEVEL_DELETE'), { ids, stiModel })
  }

  protected lowLevelDeleteByCondition(condition: Contracts.Predicate<T>, stiModel?: typeof BaseModel) {
    // this.log('lowLevelDeleteByCondition')
    this.model().store().commit(this.getMutationName('LOW_LEVEL_DELETE_CONDITION'), { condition, stiModel })
  }

  protected getMutationName(name: string) {
    return [name, this.model().entity].join('.')
  }

  protected getBaseEntity() {
    return this.model().store().$db().baseModel(this.model().entity)
  }

  public isEmptyStore() {
    return this.query().first() === null
  }

  public hasSingleSetting() {
    return !!this.settings().single
  }

  protected abstract getPath(key: R, id: string): string
  protected abstract serializeData(data: ModelRaw<Partial<T>>): { [key: string]: any }

  /**
   * Logger
   */
  protected log(message: any) {
    if (this.loggerService.shouldLogByChannel('orm', [this.model().entity])) {
      this.loggerService.log('orm', isString(message) ? message : JSON.stringify(message), this.model().entity)
    }
  }

  protected logGroupStart(message: string) {
    if (this.loggerService.shouldLogByChannel('orm', [this.model().entity])) {
      this.loggerService.groupStart('orm', message, this.model().entity)
    }
  }

  protected logArgs(args: string) {
    if (this.loggerService.shouldLogByChannel('orm', [this.model().entity])) {
      this.loggerService.args('orm', args, this.model().entity)
    }
  }

  protected logGroupEnd() {
    if (this.loggerService.shouldLogByChannel('orm', [this.model().entity])) {
      this.loggerService.groupEnd('orm')
    }
  }
}
